import { useMemo, useState } from "react";
import { useDebouncedValue } from "./useDebouncedValue";
import {
  useListClientsQuery,
  useGetClientQuery,
  useListProvidersQuery,
  useGetProviderQuery,
  useListLocationsQuery,
  useGetLocationQuery,
} from "../features/masterdata/masterdataApi";
import type { Client, Provider, Location } from "../features/masterdata/types";

const PAGE_SIZE = 5;
const DEBOUNCE_MS = 350;

export type UseMasterdataSearchOptions = {
  /** When false, list query is skipped (no fetch until dropdown opens). */
  menuOpen?: boolean;
  /** When provided, search is controlled by parent (per-stop isolation). */
  controlledSearchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  /** Stable key per stop; isolates query cache so each stop has its own results. */
  stopKey?: string;
};

function useSearchBase<T>(
  selectedId: string | null,
  menuOpen: boolean,
  listQuery: { data?: { results?: T[] }; isLoading: boolean },
  getQuery: { data?: T; isFetching: boolean }
) {
  const options = useMemo(() => {
    const list = listQuery.data?.results ?? [];
    if (!selectedId || !getQuery.data) return list;
    if (list.some((o) => (o as { id: string }).id === selectedId)) return list;
    return [getQuery.data, ...list];
  }, [listQuery.data?.results, getQuery.data, selectedId]);
  const loading = listQuery.isLoading || (!!selectedId && getQuery.isFetching && !getQuery.data);
  return { options, loading };
}

export function useClientSearch(selectedId: string | null, opts: UseMasterdataSearchOptions = {}) {
  const { menuOpen = false } = opts;
  const [searchTerm, setSearchTerm] = useState("");
  const debounced = useDebouncedValue(searchTerm, DEBOUNCE_MS);
  const listQ = useListClientsQuery(
    {
      search: debounced.trim() || undefined,
      page_size: PAGE_SIZE,
      ordering: "name",
    },
    { skip: !menuOpen }
  );
  const getQ = useGetClientQuery(selectedId!, { skip: !selectedId });
  const { options, loading } = useSearchBase<Client>(selectedId, menuOpen, listQ, getQ);
  return { options, loading, searchTerm, setSearchTerm };
}

export function useProviderSearch(selectedId: string | null, opts: UseMasterdataSearchOptions = {}) {
  const { menuOpen = false } = opts;
  const [searchTerm, setSearchTerm] = useState("");
  const debounced = useDebouncedValue(searchTerm, DEBOUNCE_MS);
  const listQ = useListProvidersQuery(
    {
      search: debounced.trim() || undefined,
      page_size: PAGE_SIZE,
      ordering: "name",
    },
    { skip: !menuOpen }
  );
  const getQ = useGetProviderQuery(selectedId!, { skip: !selectedId });
  const { options, loading } = useSearchBase<Provider>(selectedId, menuOpen, listQ, getQ);
  return { options, loading, searchTerm, setSearchTerm };
}

export function useLocationSearch(selectedId: string | null, opts: UseMasterdataSearchOptions = {}) {
  const { menuOpen = false, controlledSearchTerm, onSearchTermChange, stopKey } = opts;
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const isControlled = controlledSearchTerm !== undefined && onSearchTermChange !== undefined;
  const searchTerm = isControlled ? controlledSearchTerm : internalSearchTerm;
  const setSearchTerm = isControlled ? onSearchTermChange! : setInternalSearchTerm;
  const debounced = useDebouncedValue(searchTerm, DEBOUNCE_MS);
  const listQ = useListLocationsQuery(
    {
      search: debounced.trim() || undefined,
      page_size: PAGE_SIZE,
      ordering: "name",
      stopKey,
    },
    { skip: !menuOpen }
  );
  const getQ = useGetLocationQuery(selectedId!, { skip: !selectedId });
  const { options, loading } = useSearchBase<Location>(selectedId, menuOpen, listQ, getQ);
  return { options, loading, searchTerm, setSearchTerm };
}
